import React, { useMemo } from 'react';
import { useAppDispatch } from '../app/redux/useAppSelector';
import FavoriteIcon from '../components/FavoriteIcon';
import { MenuItem } from '../components/Menu';
import reorderIcon from '../icons/non-prefilled/16x16/reorder.svg';
import useFavoritePosts from '../post/useFavoritePosts';
import { filterTreeListTopics } from '../topic/toTopicTreeList';
import useFavoriteTopics from '../topic/useFavoriteTopics';
import ReorderFavoritesModal from './ReorderFavoritesModal';
import SidebarItems from './SidebarItems';
import SidebarSection from './SidebarSection';
import isNotDetached from './isNotDetached';
import sortSidebarItems from './sortSidebarItems';
import useSidebarSettings from './useSidebarSettings';
import { UIActions } from '../ui/uiSlice';

interface FavoritesProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
}

const Favorites = ({ activeTopics, expandedTopics }: FavoritesProps) => {
  const favoritePosts = useFavoritePosts();

  const allFavoriteTopics = useFavoriteTopics();
  const favoriteTopics = useMemo(
    () =>
      filterTreeListTopics(
        allFavoriteTopics,
        (topic, level) => level === 0 || isNotDetached(topic),
      ),
    [allFavoriteTopics],
  );

  const favoriteItemsCount = favoritePosts.length + favoriteTopics.length;

  const { favoriteIds } = useSidebarSettings();

  const dispatch = useAppDispatch();

  if (favoriteItemsCount === 0) {
    return null;
  }

  return (
    <SidebarSection
      id="favorites"
      icon={<FavoriteIcon prefilled active />}
      title="Favorites"
      menuItems={
        favoriteTopics.length + favoritePosts.length > 1 && (
          <MenuItem
            icon={reorderIcon}
            onClick={() => {
              dispatch(
                UIActions.openModal(ReorderFavoritesModal, {
                  favorites: sortSidebarItems(
                    [...favoriteTopics, ...favoritePosts],
                    favoriteIds,
                  ),
                }),
              );
            }}
          >
            Reorder favorites
          </MenuItem>
        )
      }
    >
      <SidebarItems
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
        posts={favoritePosts}
        topics={favoriteTopics}
        orderIds={favoriteIds}
      />
    </SidebarSection>
  );
};

export default Favorites;
